/* YPB consolidated product JS: purchase module + story interactions */
(() => {
  class YPBPurchaseModule extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ypbReady === 'true') return;
      this.dataset.ypbReady = 'true';

      this.productId = String(this.dataset.productId || '');
      this.scope = this.parentElement || document;
      this.selectedQuantity = Number(this.dataset.defaultQuantity) || 1;
      this.currentPurchase = 'one_time';
      this.currentVariantId = String(this.dataset.initialVariantId || '');
      this.quantityButtons = Array.from(this.querySelectorAll('[data-ypb-quantity]'));
      this.purchaseButtons = Array.from(this.querySelectorAll('[data-ypb-purchase-type]'));
      this.planButtons = Array.from(this.querySelectorAll('[data-ypb-selling-plan]'));
      this.frequencyPanel = this.querySelector('[data-ypb-frequency]');
      this.activePlanId = this.planButtons[0]?.dataset.ypbSellingPlan || '';
      this.pricing = this.readPricing();
      this.hiddenLegacyElements = new Set();
      this.handleScopeChange = this.onScopeChange.bind(this);

      this.bindControls();
      this.scope.addEventListener('change', this.handleScopeChange);
      this.revealNativeProductForms();
      this.hideNativeQuantity();
      this.suppressSealUI();
      this.updateCurrentVariantFromNativeForm();
      this.updatePlanSavings();
      this.applyInitialState();

      this.observer = new MutationObserver(() => {
        this.revealNativeProductForms();
        this.hideNativeQuantity();
        this.suppressSealUI();
        this.updateCurrentVariantFromNativeForm();
        this.syncNativeForms();
      });
      this.observer.observe(this.scope, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.scope?.removeEventListener('change', this.handleScopeChange);
      this.hiddenLegacyElements?.forEach((element) => element.classList.remove('ypb-native-control-hidden'));
      this.hiddenLegacyElements?.clear();
    }

    readPricing() {
      try {
        return JSON.parse(this.querySelector('[data-ypb-pricing]')?.textContent || '{}');
      } catch (error) {
        console.warn('YPB Purchase Module pricing data could not be read.', error);
        return {};
      }
    }

    bindControls() {
      this.quantityButtons.forEach((button) => {
        button.addEventListener('click', () => this.selectQuantity(Number(button.dataset.ypbQuantity) || 1));
      });

      this.purchaseButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          if (button.hasAttribute('data-ypb-subscription-toggle')) {
            this.selectPurchaseType(this.currentPurchase === 'subscription' ? 'one_time' : 'subscription');
          } else {
            this.selectPurchaseType(button.dataset.ypbPurchaseType);
          }
        });
      });

      this.planButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.activePlanId = String(button.dataset.ypbSellingPlan || '');
          this.selectPurchaseType('subscription');
          this.applyActivePlanState();
          this.syncNativeForms();
          this.updatePrices();
        });
      });
    }

    applyInitialState() {
      this.selectQuantity(this.selectedQuantity);
      const wantsSubscription = this.dataset.defaultPurchase === 'subscription' && this.planButtons.length > 0;
      this.selectPurchaseType(wantsSubscription ? 'subscription' : 'one_time');
    }

    selectQuantity(quantity) {
      this.selectedQuantity = Math.max(1, Number(quantity) || 1);
      this.quantityButtons.forEach((button) => {
        const selected = Number(button.dataset.ypbQuantity) === this.selectedQuantity;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      this.syncNativeForms();
      this.updatePrices();
    }

    selectPurchaseType(type) {
      const subscriptionAvailable = this.planButtons.length > 0;
      this.currentPurchase = type === 'subscription' && subscriptionAvailable ? 'subscription' : 'one_time';
      this.dataset.currentPurchase = this.currentPurchase;

      this.purchaseButtons.forEach((button) => {
        const selected = this.currentPurchase === 'subscription'
          ? button.dataset.ypbPurchaseType === 'subscription'
          : button.dataset.ypbPurchaseType === 'one_time';
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      if (this.currentPurchase === 'subscription') {
        if (!this.activePlanId && this.planButtons[0]) this.activePlanId = String(this.planButtons[0].dataset.ypbSellingPlan || '');
        this.applyActivePlanState();
      }

      if (this.frequencyPanel) {
        const always = this.dataset.subscriptionDisplay === 'always';
        this.frequencyPanel.hidden = !always && this.currentPurchase !== 'subscription';
        this.frequencyPanel.classList.toggle('is-inactive', always && this.currentPurchase !== 'subscription');
      }

      this.syncNativeForms();
      this.updatePrices();
    }

    applyActivePlanState() {
      this.planButtons.forEach((button) => {
        const selected = String(button.dataset.ypbSellingPlan) === String(this.activePlanId);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    findNativeProductForms() {
      if (!this.scope?.querySelectorAll) return [];
      return Array.from(this.scope.querySelectorAll('product-form[data-product-id]')).filter((productForm) => {
        if (this.contains(productForm) || productForm.closest('ypb-purchase-module')) return false;
        return String(productForm.dataset.productId || '') === this.productId;
      });
    }

    revealNativeProductForms() {
      this.findNativeProductForms().forEach((productForm) => {
        productForm.classList.remove('ypb-native-control-hidden');
        this.hiddenLegacyElements.delete(productForm);
      });
    }

    hideNativeQuantity() {
      if (!this.scope?.querySelectorAll) return;
      this.scope.querySelectorAll('quantity-input[data-product-id]').forEach((control) => {
        if (String(control.dataset.productId || '') !== this.productId) return;
        control.classList.add('ypb-native-control-hidden');
        this.hiddenLegacyElements.add(control);
      });
    }

    suppressSealUI() {
      if (!this.scope?.querySelectorAll) return;
      this.scope.querySelectorAll('.sealsubs-target-element, .sealsubs-container').forEach((element) => {
        if (element.querySelector?.(`product-form[data-product-id="${this.productId}"]`)) return;
        element.classList.add('ypb-native-control-hidden');
        this.hiddenLegacyElements.add(element);
      });
    }

    syncNativeForms() {
      this.findNativeProductForms().forEach((productForm) => {
        const form = productForm.querySelector('form');
        if (!form) return;

        let quantityInput = form.querySelector('input[name="quantity"]');
        if (!quantityInput) {
          quantityInput = document.createElement('input');
          quantityInput.type = 'hidden';
          quantityInput.name = 'quantity';
          form.appendChild(quantityInput);
        }
        quantityInput.value = String(this.selectedQuantity);
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

        let sellingPlanInput = form.querySelector('input[name="selling_plan"][data-ypb-controlled-selling-plan]');
        if (!sellingPlanInput) {
          sellingPlanInput = document.createElement('input');
          sellingPlanInput.type = 'hidden';
          sellingPlanInput.name = 'selling_plan';
          sellingPlanInput.dataset.ypbControlledSellingPlan = 'true';
          form.appendChild(sellingPlanInput);
        }

        form.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
          if (input !== sellingPlanInput) input.disabled = true;
        });

        if (this.currentPurchase === 'subscription' && this.activePlanId) {
          sellingPlanInput.value = String(this.activePlanId);
          sellingPlanInput.disabled = false;
        } else {
          sellingPlanInput.value = '';
          sellingPlanInput.disabled = true;
        }
        sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateCurrentVariantFromNativeForm() {
      const nativeForm = this.findNativeProductForms()[0];
      const variantId = nativeForm?.querySelector('form input[name="id"]')?.value;
      if (variantId) this.currentVariantId = String(variantId);
    }

    onScopeChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.name !== 'id') return;
      const productForm = target.closest('product-form[data-product-id]');
      if (!productForm || String(productForm.dataset.productId || '') !== this.productId) return;
      this.currentVariantId = String(target.value || this.currentVariantId);
      this.updatePlanSavings();
      this.updatePrices();
      this.syncNativeForms();
    }

    getVariantPricing() {
      return this.pricing?.variants?.[this.currentVariantId]
        || this.pricing?.variants?.[String(this.dataset.initialVariantId || '')]
        || Object.values(this.pricing?.variants || {})[0]
        || { regularUnit: 0, plans: {} };
    }

    getBundlePercent(quantity) {
      if (quantity < 2 || !this.pricing.applyBundlePreview) return 0;
      if (this.currentPurchase === 'subscription') {
        return Math.max(0, Math.min(100, Number(this.pricing.subscriptionBundlePercent || 0)));
      }
      return Math.max(0, Math.min(100, Number(this.pricing.bundlePercents?.[String(quantity)] || 0)));
    }

    calculate(quantity) {
      const variantPricing = this.getVariantPricing();
      const regularUnit = Number(variantPricing.regularUnit || 0);
      const regularTotal = regularUnit * quantity;
      let pricedUnit = regularUnit;

      if (this.currentPurchase === 'subscription' && this.activePlanId) {
        pricedUnit = Number(variantPricing.plans?.[this.activePlanId] || regularUnit);
      }

      const preBundleTotal = pricedUnit * quantity;
      const bundlePercent = this.getBundlePercent(quantity);
      const currentTotal = bundlePercent > 0
        ? Math.round(preBundleTotal * (100 - bundlePercent) / 100)
        : preBundleTotal;

      return {
        regularUnit,
        regularTotal,
        currentTotal,
        currentUnit: quantity > 0 ? Math.round(currentTotal / quantity) : currentTotal,
        saving: Math.max(0, regularTotal - currentTotal),
        bundlePercent
      };
    }

    updatePrices() {
      const variantPricing = this.getVariantPricing();
      if (!Number(variantPricing.regularUnit || 0)) return;
      const style = this.dataset.priceStyle || 'total';

      this.quantityButtons.forEach((button) => {
        const quantity = Number(button.dataset.ypbQuantity) || 1;
        const price = this.calculate(quantity);
        const currentNode = button.querySelector('[data-ypb-current-price]');
        const savingNode = button.querySelector('[data-ypb-saving]');

        if (currentNode) currentNode.textContent = this.formatMoney(style === 'per_bottle' ? price.currentUnit : price.currentTotal);
        if (savingNode) {
          if (price.saving > 0 && this.currentPurchase === 'one_time' && price.bundlePercent > 0) {
            savingNode.textContent = `${price.bundlePercent}% OFF · Save ${this.formatMoney(price.saving)}`;
          } else {
            savingNode.textContent = price.saving > 0 ? `Save ${this.formatMoney(price.saving)}` : '';
          }
          savingNode.hidden = price.saving <= 0;
        }
      });
    }

    updatePlanSavings() {
      const variantPricing = this.getVariantPricing();
      const regularUnit = Number(variantPricing.regularUnit || 0);
      if (!regularUnit) return;
      this.planButtons.forEach((button) => {
        const planId = String(button.dataset.ypbSellingPlan || '');
        const planUnit = Number(variantPricing.plans?.[planId] || regularUnit);
        const percent = Math.max(0, Math.round((1 - (planUnit / regularUnit)) * 100));
        const node = button.querySelector('[data-ypb-plan-saving]');
        if (node) node.textContent = percent > 0 ? `Save ${percent}%` : '';
      });
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || 'AUD';
      const locale = this.dataset.locale || undefined;
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }
  }

  if (!customElements.get('ypb-purchase-module')) customElements.define('ypb-purchase-module', YPBPurchaseModule);
})();

(() => {
  if (!customElements.get('ypb-choice')) customElements.define('ypb-choice', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return; this.ready = true;
      this.buttons = [...this.querySelectorAll('[data-choice]')];
      this.panels = [...this.querySelectorAll('[data-choice-panel]')];
      this.buttons.forEach(button => {
        button.addEventListener('click', () => this.select(button.dataset.choice));
        if (button.getAttribute('role') === 'tab') button.addEventListener('keydown', event => {
          const tabs = this.buttons.filter(b => b.getAttribute('role') === 'tab');
          let next = tabs.indexOf(button);
          if (event.key === 'ArrowRight') next = (next + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') next = (next - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else return;
          event.preventDefault(); this.select(tabs[next].dataset.choice); tabs[next].focus();
        });
      });
    }
    select(value) {
      this.buttons.forEach(button => {
        const chosen = button.dataset.choice === value;
        if (button.getAttribute('role') === 'tab') { button.setAttribute('aria-selected', chosen); button.tabIndex = chosen ? 0 : -1; }
        else button.setAttribute('aria-pressed', chosen);
      });
      this.panels.forEach(panel => { panel.hidden = panel.dataset.choicePanel !== value; });
    }
  });

  if (!customElements.get('ypb-cart-callout')) customElements.define('ypb-cart-callout', class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-ypb-add]');
      this.selection = this.querySelector('[data-ypb-selection]');
      this.error = this.querySelector('[data-ypb-error]');
      this.sync = this.update.bind(this);
      this.submit = this.add.bind(this);
      this.button.addEventListener('click', this.submit);
      document.addEventListener('change', this.sync);
      document.addEventListener('click', this.sync);
      document.addEventListener('shopify:section:load', this.sync);
      this.update();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.button.removeEventListener('click', this.submit);
      document.removeEventListener('change', this.sync);
      document.removeEventListener('click', this.sync);
      document.removeEventListener('shopify:section:load', this.sync);
    }
    update() {
      const module = [...document.querySelectorAll('ypb-purchase-module')].find(el => el.dataset.productId === this.dataset.productId);
      const productForm = module?.findNativeProductForms?.()[0];
      const form = productForm?.querySelector('form');
      const nativeButton = form?.querySelector('[type="submit"]');
      if (!form || !nativeButton) { this.button.hidden = true; this.querySelector('[data-ypb-fallback]').hidden = false; return; }
      if (form !== this.form) {
        this.observer?.disconnect(); this.form = form; this.productForm = productForm;
        this.observer = new MutationObserver(this.sync);
        this.observer.observe(form, { attributes: true, attributeFilter: ['data-state','disabled'], subtree: true });
      }
      const loading = form.dataset.state === 'loading';
      const unavailable = nativeButton.disabled || nativeButton.getAttribute('aria-disabled') === 'true';
      this.button.hidden = false; this.querySelector('[data-ypb-fallback]').hidden = true;
      this.button.disabled = loading || unavailable;
      this.button.setAttribute('aria-busy', String(loading));
      this.button.textContent = loading ? 'Adding…' : unavailable ? this.dataset.soldLabel : this.dataset.addLabel + ' ↗';
      const quantity = module.selectedQuantity || Number(form.querySelector('[name="quantity"]')?.value) || 1;
      const purchase = module.currentPurchase === 'subscription' ? 'Subscription' : 'One-time purchase';
      const plan = module.currentPurchase === 'subscription' ? module.querySelector('[data-ypb-selling-plan][aria-pressed="true"]')?.textContent.trim() : '';
      this.selection.textContent = quantity + (quantity === 1 ? ' bottle · ' : ' bottles · ') + purchase + (plan ? ' · ' + plan : '');
      if (this.pending && !loading) {
        this.pending = false;
        this.error.textContent = this.productForm.errorMessage?.textContent || '';
      }
    }
    add() {
      this.update(); if (!this.form || this.button.disabled) return;
      this.error.textContent = ''; this.pending = true;
      this.productForm.handleErrorMessage?.();
      this.form.requestSubmit(this.form.querySelector('[type="submit"]'));
      this.update();
    }
  });

  const bindExclusiveDetails = (root = document) => {
    root.querySelectorAll('details[data-ypb-exclusive-group]').forEach(details => {
      if (details.dataset.ypbExclusiveBound === 'true') return;
      details.dataset.ypbExclusiveBound = 'true';
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        const group = details.dataset.ypbExclusiveGroup;
        const scope = details.closest('[data-ypb-accordion-scope]') || document;
        scope.querySelectorAll('details[data-ypb-exclusive-group]').forEach(other => {
          if (other !== details && other.dataset.ypbExclusiveGroup === group) other.open = false;
        });
      });
    });
  };

  const initExclusiveDetails = event => bindExclusiveDetails(event?.target || document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bindExclusiveDetails(document), { once: true });
  } else {
    bindExclusiveDetails(document);
  }
  document.addEventListener('shopify:section:load', initExclusiveDetails);
})();

(() => {
  const providers = [
    {
      key: 'klarna',
      defaultBrand: 'Klarna',
      hiddenClass: 'ypb-klarna-native-hidden',
      selectors: [
        'klarna-placement',
        '[data-key*="credit-promotion"]',
        '[class*="klarna" i]',
        '[id*="klarna" i]'
      ]
    },
    {
      key: 'afterpay',
      defaultBrand: 'Afterpay',
      hiddenClass: 'ypb-afterpay-native-hidden',
      selectors: [
        'afterpay-placement',
        'square-placement',
        '[class*="afterpay" i]',
        '[id*="afterpay" i]',
        '[class*="clearpay" i]',
        '[id*="clearpay" i]'
      ]
    }
  ];

  const paymentMethods = [
    { key: 'afterpay', label: 'Afterpay' },
    { key: 'klarna', label: 'Klarna' },
    { key: 'apple-pay', label: 'Apple Pay' },
    { key: 'mastercard', label: 'Mastercard' },
    { key: 'visa', label: 'Visa' },
    { key: 'amex', label: 'American Express' },
    { key: 'unionpay', label: 'UnionPay' },
    { key: 'jcb', label: 'JCB' }
  ];

  let scheduled = false;

  function isProductPage() {
    return document.body?.dataset?.pageType === 'product';
  }

  function getMainProductScope() {
    return document.querySelector('main .shopify-section.relative.z-layout') || document.querySelector('main');
  }

  function ensureLayoutStyles() {
    if (document.getElementById('ypb-pdp-layout-refine-v3')) return;
    const style = document.createElement('style');
    style.id = 'ypb-pdp-layout-refine-v3';
    style.textContent = `
      body[data-page-type="product"] .ypb-benefits--product {
        width: 100% !important;
        display: grid !important;
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        gap: 7px !important;
        margin: 0 0 14px !important;
        padding: 0 !important;
        border: 0 !important;
        list-style: none !important;
      }
      body[data-page-type="product"] .ypb-benefits--product li {
        min-width: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 8px !important;
        margin: 0 !important;
        padding: 8px 9px !important;
        border: 1px solid #f1dbe2 !important;
        border-radius: 12px !important;
        background: #fff9fb !important;
        text-align: left !important;
      }
      body[data-page-type="product"] .ypb-benefits--product .ypb-icon {
        width: 28px !important;
        height: 28px !important;
        min-width: 28px !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        margin: 0 !important;
      }
      body[data-page-type="product"] .ypb-benefits--product li > div {
        min-width: 0 !important;
        text-align: left !important;
      }
      body[data-page-type="product"] .ypb-benefits--product strong {
        display: block !important;
        margin: 0 !important;
        color: #2e4a45 !important;
        font-size: 11px !important;
        font-weight: 800 !important;
        line-height: 1.18 !important;
      }
      body[data-page-type="product"] .ypb-benefits--product li > div > span {
        display: none !important;
      }
      body[data-page-type="product"] ypb-purchase-module > .ypb-pm__areas {
        margin-bottom: 10px !important;
      }
      body[data-page-type="product"] .ypb-pm__custom-atc-wrap {
        width: 100%;
        margin-top: 14px;
      }
      body[data-page-type="product"] .ypb-pm__custom-atc {
        width: 100%;
        min-height: 54px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 13px 18px;
        border: 2px solid var(--ypb-atc-bg, #2e4a45);
        border-radius: var(--ypb-card-radius, 14px);
        background: var(--ypb-atc-bg, #2e4a45);
        color: var(--ypb-atc-text, #ffffff);
        font: 800 16px/1.1 var(--theme-body-font-family), sans-serif;
        letter-spacing: .01em;
        cursor: pointer;
        transition: transform .16s ease, opacity .16s ease, background-color .16s ease;
      }
      body[data-page-type="product"] .ypb-pm__custom-atc:hover:not(:disabled) {
        transform: translateY(-1px);
        border-color: var(--ypb-atc-hover, #243d39);
        background: var(--ypb-atc-hover, #243d39);
      }
      body[data-page-type="product"] .ypb-pm__custom-atc:focus-visible {
        outline: 3px solid #f4bed3;
        outline-offset: 3px;
      }
      body[data-page-type="product"] .ypb-pm__custom-atc:disabled {
        cursor: not-allowed;
        opacity: .48;
      }
      body[data-page-type="product"] .ypb-native-atc-hidden {
        display: none !important;
      }
      body[data-page-type="product"] .ypb-payment-options.ypb-payment-options--after-atc {
        margin: 10px 0 0 !important;
      }
      body[data-page-type="product"] .ypb-pm__section--purchase > .ypb-atc-trust {
        margin: 10px 0 0 !important;
      }
      body[data-page-type="product"] main .shopify-section.relative.z-layout:has(ypb-purchase-module) > .section-padding {
        padding-bottom: 4px !important;
      }
      body[data-page-type="product"] #ypb-where-to-use.ypb-story {
        margin-top: 0 !important;
        padding-top: 0 !important;
      }
      body[data-page-type="product"] #ypb-where-to-use .ypb-story__wrap {
        padding-top: 0 !important;
      }
      @media (max-width: 749px) {
        body[data-page-type="product"] .ypb-benefits--product {
          gap: 6px !important;
          margin-bottom: 10px !important;
        }
        body[data-page-type="product"] .ypb-benefits--product li {
          gap: 6px !important;
          padding: 7px !important;
          border-radius: 10px !important;
        }
        body[data-page-type="product"] .ypb-benefits--product .ypb-icon {
          width: 24px !important;
          height: 24px !important;
          min-width: 24px !important;
        }
        body[data-page-type="product"] .ypb-benefits--product strong {
          font-size: 9.5px !important;
          line-height: 1.15 !important;
        }
        body[data-page-type="product"] .ypb-pm__custom-atc-wrap {
          margin-top: 12px;
        }
        body[data-page-type="product"] .ypb-pm__custom-atc {
          min-height: 50px;
          padding: 12px 14px;
          font-size: 15px;
          border-radius: 13px;
        }
        body[data-page-type="product"] .ypb-payment-options.ypb-payment-options--after-atc {
          margin: 8px 0 0 !important;
        }
        body[data-page-type="product"] main .shopify-section.relative.z-layout:has(ypb-purchase-module) > .section-padding {
          padding-bottom: 0 !important;
        }
        body[data-page-type="product"] #ypb-where-to-use.ypb-story {
          padding-top: 0 !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function getDeepText(element) {
    if (!element) return '';
    let text = element.textContent || '';
    const seenRoots = new Set();
    function addShadowText(root) {
      if (!root || seenRoots.has(root)) return;
      seenRoots.add(root);
      text += ` ${root.textContent || ''}`;
      root.querySelectorAll?.('*').forEach((child) => {
        if (child.shadowRoot) addShadowText(child.shadowRoot);
      });
    }
    if (element.shadowRoot) addShadowText(element.shadowRoot);
    element.querySelectorAll?.('*').forEach((child) => {
      if (child.shadowRoot) addShadowText(child.shadowRoot);
    });
    return text.replace(/\s+/g, ' ').trim();
  }

  function extractPaymentCount(text) {
    const normalized = String(text || '').replace(/\s+/g, ' ').trim();
    const numberMatch = normalized.match(/\b(\d+)\s+(?:interest[- ]free\s+)?(?:payments?|installments?|instalments?)/i);
    if (numberMatch) return numberMatch[1];
    const payInMatch = normalized.match(/\bpay\s+in\s+(\d+)\b/i);
    if (payInMatch) return payInMatch[1];
    const wordMap = { one: '1', two: '2', three: '3', four: '4', five: '5', six: '6', seven: '7', eight: '8' };
    const wordMatch = normalized.match(/\b(one|two|three|four|five|six|seven|eight)\s+(?:interest[- ]free\s+)?(?:payments?|installments?|instalments?)/i);
    if (wordMatch) return wordMap[wordMatch[1].toLowerCase()] || null;
    return null;
  }

  function collectKlarnaFallback(provider) {
    if (provider.key !== 'klarna' || !document.getElementById('ypb-payment-logo-klarna')) return null;
    return { provider, nativeWrapper: null, nativeText: '', count: null, displayBrand: provider.defaultBrand };
  }

  function isCustomElement(element) {
    return Boolean(element?.closest?.('.ypb-payment-options, .ypb-payment-compact'));
  }

  function looksLikeProvider(element, provider) {
    if (!element || isCustomElement(element)) return false;
    const tagName = element.tagName?.toLowerCase() || '';
    if (provider.key === 'klarna' && tagName === 'klarna-placement') return true;
    if (provider.key === 'afterpay' && (tagName === 'afterpay-placement' || tagName === 'square-placement')) return true;
    const text = getDeepText(element).toLowerCase();
    if (provider.key === 'afterpay') return text.includes('afterpay') || text.includes('clearpay');
    return text.includes('klarna');
  }

  function findPlacement(scope, provider) {
    if (!scope) return null;
    const candidates = [];
    const seen = new Set();
    provider.selectors.forEach((selector) => {
      scope.querySelectorAll(selector).forEach((element) => {
        if (seen.has(element)) return;
        seen.add(element);
        candidates.push(element);
      });
    });
    return candidates.find((element) => looksLikeProvider(element, provider)) || null;
  }

  function findNativeWrapper(placement) {
    if (!placement) return null;
    const appBlock = placement.closest?.('.shopify-app-block, [id^="shopify-block-"]');
    if (appBlock && !isCustomElement(appBlock)) return appBlock;
    const parent = placement.parentElement;
    if (parent && !isCustomElement(parent)) return parent;
    return placement;
  }

  function hasProviderOutput(placement, nativeWrapper, provider, nativeText) {
    if (!placement || !nativeWrapper) return false;
    if (nativeWrapper.classList.contains(provider.hiddenClass)) return true;
    const normalized = String(nativeText || '').toLowerCase();
    if (provider.key === 'klarna' && normalized.includes('klarna')) return true;
    if (provider.key === 'afterpay' && (normalized.includes('afterpay') || normalized.includes('clearpay'))) return true;
    const rect = placement.getBoundingClientRect?.();
    return Boolean(rect && rect.width > 20 && rect.height > 8);
  }

  function getDisplayBrand(provider, nativeText) {
    const normalized = String(nativeText || '').toLowerCase();
    if (provider.key === 'afterpay' && normalized.includes('clearpay')) return 'Clearpay';
    return provider.defaultBrand;
  }

  function collectProviderState(scope, provider) {
    const placement = findPlacement(scope, provider);
    if (!placement) return null;
    const nativeWrapper = findNativeWrapper(placement);
    if (!nativeWrapper || isCustomElement(nativeWrapper)) return null;
    const nativeText = getDeepText(nativeWrapper) || getDeepText(placement);
    if (!hasProviderOutput(placement, nativeWrapper, provider, nativeText)) return null;
    return {
      provider,
      placement,
      nativeWrapper,
      nativeText,
      count: extractPaymentCount(nativeText),
      displayBrand: getDisplayBrand(provider, nativeText)
    };
  }

  function createOptionsShell() {
    const shell = document.createElement('section');
    shell.className = 'ypb-payment-options';
    shell.dataset.ypbPaymentOptions = '';
    shell.setAttribute('aria-label', 'Flexible payment options');
    const header = document.createElement('div');
    header.className = 'ypb-payment-options__header';
    const heading = document.createElement('span');
    heading.className = 'ypb-payment-options__heading';
    heading.textContent = 'Pay your way';
    const helper = document.createElement('span');
    helper.className = 'ypb-payment-options__helper';
    helper.textContent = 'Choose at checkout';
    const list = document.createElement('div');
    list.className = 'ypb-payment-options__list';
    paymentMethods.forEach((method) => {
      const item = document.createElement('span');
      item.className = `ypb-payment-method ypb-payment-method--${method.key}`;
      item.setAttribute('role', 'img');
      item.setAttribute('aria-label', method.label);
      const logo = document.getElementById(`ypb-payment-logo-${method.key}`)?.content?.querySelector('svg');
      if (logo) {
        const image = logo.cloneNode(true);
        image.setAttribute('aria-hidden', 'true');
        image.setAttribute('focusable', 'false');
        item.appendChild(image);
      } else {
        item.textContent = method.label;
      }
      list.appendChild(item);
    });
    header.append(heading, helper);
    shell.append(header, list);
    return shell;
  }

  function unhideNative(scope) {
    providers.forEach((provider) => {
      scope.querySelectorAll(`.${provider.hiddenClass}`).forEach((element) => {
        element.classList.remove(provider.hiddenClass);
        element.removeAttribute('aria-hidden');
      });
    });
  }

  function findAddToCart(scope, purchaseModule) {
    const productId = String(purchaseModule?.dataset?.productId || '');
    const nativeProductForm = Array.from(scope.querySelectorAll('product-form[data-product-id]'))
      .find((element) => {
        if (element.closest('ypb-purchase-module')) return false;
        if (!productId) return true;
        return String(element.dataset.productId || '') === productId;
      });
    const form = nativeProductForm?.querySelector('form');
    const addButton = form?.querySelector('button[name="add"]') || Array.from(scope.querySelectorAll('button[name="add"]')).find((button) => !button.closest('ypb-purchase-module'));
    const buttonWrap = addButton?.closest('.w-full') || addButton?.parentElement || addButton;
    return { nativeProductForm, form, addButton, buttonWrap };
  }

  function ensureCustomAddToCart(scope, purchaseModule) {
    if (!purchaseModule) return null;
    const purchaseSection = purchaseModule.querySelector('.ypb-pm__section--purchase');
    if (!purchaseSection) return null;

    let wrap = purchaseSection.querySelector('[data-ypb-custom-atc-wrap]');
    let button = wrap?.querySelector('[data-ypb-custom-atc]');

    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'ypb-pm__custom-atc-wrap';
      wrap.dataset.ypbCustomAtcWrap = '';
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'ypb-pm__custom-atc';
      button.dataset.ypbCustomAtc = '';
      button.textContent = 'Add to cart';
      button.addEventListener('click', () => {
        const current = findAddToCart(scope, purchaseModule);
        if (!current.addButton || current.addButton.disabled) return;
        current.addButton.click();
      });
      wrap.appendChild(button);
      purchaseSection.appendChild(wrap);
    }

    const native = findAddToCart(scope, purchaseModule);
    if (native.buttonWrap) native.buttonWrap.classList.add('ypb-native-atc-hidden');

    if (button) {
      const loading = native.nativeProductForm?.dataset?.state === 'loading';
      const disabled = !native.addButton || native.addButton.disabled || loading;
      const desiredText = loading ? 'Adding…' : (native.addButton?.disabled ? 'Sold out' : 'Add to cart');
      if (button.disabled !== disabled) button.disabled = disabled;
      if (button.textContent !== desiredText) button.textContent = desiredText;
      button.setAttribute('aria-label', desiredText);
    }

    return wrap;
  }

  function moveBenefitsUnderTargets(scope, purchaseModule, areas) {
    if (!purchaseModule || !areas) return;
    const benefits = document.querySelector('#ypb-where-to-use .ypb-benefits') || document.querySelector('.ypb-benefits--product');
    if (!benefits) return;
    benefits.classList.add('ypb-benefits--product');
    benefits.querySelectorAll('li > div > span').forEach((node) => node.setAttribute('aria-hidden', 'true'));
    if (areas.nextElementSibling !== benefits) areas.insertAdjacentElement('afterend', benefits);
  }

  function repositionPdpElements(scope, shell) {
    ensureLayoutStyles();
    const purchaseModule = scope.querySelector('ypb-purchase-module');
    const areas = scope.querySelector('.ypb-pm__areas');
    const purchaseSection = purchaseModule?.querySelector('.ypb-pm__section--purchase');

    if (purchaseModule && areas) {
      if (areas.parentElement !== purchaseModule || purchaseModule.firstElementChild !== areas) {
        purchaseModule.insertAdjacentElement('afterbegin', areas);
      }
      moveBenefitsUnderTargets(scope, purchaseModule, areas);
    }

    const customAtcWrap = ensureCustomAddToCart(scope, purchaseModule);
    const { buttonWrap } = findAddToCart(scope, purchaseModule);
    const shopPayRow = scope.querySelector('.ypb-shop-pay-row');

    const purchaseColourVariables = [
      '--ypb-text',
      '--ypb-accent',
      '--ypb-border',
      '--ypb-card',
      '--ypb-selected',
      '--ypb-module-bg',
      '--ypb-atc-bg',
      '--ypb-atc-text',
      '--ypb-atc-hover',
      '--ypb-card-radius',
      '--ypb-payment-sticker-bg',
      '--ypb-payment-sticker-border',
      '--ypb-payment-sticker-text',
      '--ypb-checkout-radius'
    ];
    if (purchaseModule) {
      const purchaseStyles = getComputedStyle(purchaseModule);
      [shopPayRow, shell].filter(Boolean).forEach((element) => {
        purchaseColourVariables.forEach((property) => {
          element.style.setProperty(property, purchaseStyles.getPropertyValue(property));
        });
      });
    }

    if (customAtcWrap && shopPayRow && customAtcWrap.nextElementSibling !== shopPayRow) {
      customAtcWrap.insertAdjacentElement('afterend', shopPayRow);
    }

    if (shell) {
      shell.classList.remove('ypb-payment-options--before-atc');
      shell.classList.add('ypb-payment-options--after-atc');
      const paymentAnchor = shopPayRow || customAtcWrap || buttonWrap;
      if (paymentAnchor && paymentAnchor.nextElementSibling !== shell) {
        paymentAnchor.insertAdjacentElement('afterend', shell);
      } else if (!customAtcWrap && buttonWrap && buttonWrap.nextElementSibling !== shell) {
        buttonWrap.insertAdjacentElement('afterend', shell);
      } else if (!customAtcWrap && !buttonWrap && purchaseModule) {
        purchaseModule.insertAdjacentElement('afterend', shell);
      }
    }

    const trust = scope.querySelector('[data-ypb-atc-trust], .ypb-atc-trust');
    const trustAnchor = shell || customAtcWrap || buttonWrap;
    if (trustAnchor && trust && trustAnchor.nextElementSibling !== trust) {
      trustAnchor.insertAdjacentElement('afterend', trust);
    }

    const formula = scope.querySelector('.ypb-pm__formula');
    if (purchaseSection && formula && purchaseSection.nextElementSibling !== formula) {
      purchaseSection.insertAdjacentElement('afterend', formula);
    }
  }

  function renderPaymentOptions(scope, states) {
    scope.querySelectorAll('.ypb-payment-compact').forEach((element) => element.remove());
    let shell = scope.querySelector('[data-ypb-payment-options]');
    if (shell && !shell.querySelector('.ypb-payment-method')) {
      shell.remove();
      shell = null;
    }
    if (!shell) shell = createOptionsShell();
    repositionPdpElements(scope, shell);

    const activeKeys = new Set(states.map((state) => state.provider.key));
    states.forEach((state) => {
      if (!state.nativeWrapper) return;
      state.nativeWrapper.classList.add(state.provider.hiddenClass);
      state.nativeWrapper.setAttribute('aria-hidden', 'true');
    });

    providers.forEach((provider) => {
      if (activeKeys.has(provider.key)) return;
      scope.querySelectorAll(`.${provider.hiddenClass}`).forEach((element) => {
        element.classList.remove(provider.hiddenClass);
        element.removeAttribute('aria-hidden');
      });
    });
  }

  function decorate() {
    scheduled = false;
    if (!isProductPage()) return;
    ensureLayoutStyles();
    const scope = getMainProductScope();
    if (!scope) return;
    const states = providers
      .map((provider) => collectProviderState(scope, provider) || collectKlarnaFallback(provider))
      .filter(Boolean);
    renderPaymentOptions(scope, states);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(decorate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }

  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:block:select', schedule);
  document.addEventListener('shopify:block:deselect', schedule);

  const observer = new MutationObserver(schedule);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['disabled', 'data-state']
  });
})();

(function () {
    const defaultHeading = 'The good stuff, minus the harsh stuff';

    function buildGroup(headingText, itemTexts, duplicate) {
      const group = document.createElement('div');
      group.className = 'ypb-goodstuff-banner__group';
      if (duplicate) group.setAttribute('aria-hidden', 'true');

      const heading = document.createElement('span');
      heading.className = 'ypb-goodstuff-banner__heading';
      heading.textContent = headingText;
      group.appendChild(heading);

      itemTexts.forEach((text) => {
        const item = document.createElement('span');
        item.className = 'ypb-goodstuff-banner__item';

        const check = document.createElement('span');
        check.className = 'ypb-goodstuff-banner__check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';

        const label = document.createElement('span');
        label.textContent = text;

        item.appendChild(check);
        item.appendChild(label);
        group.appendChild(item);
      });

      return group;
    }

    function convertFormula(source) {
      if (!source) return null;
      if (source.classList.contains('ypb-goodstuff-banner') && source.querySelector('.ypb-goodstuff-banner__track')) return source;

      const headingText = source.querySelector('.ypb-pm__formula-heading')?.textContent?.trim() || defaultHeading;
      const seen = new Set();
      const itemTexts = Array.from(source.querySelectorAll('.ypb-pm__formula-item'))
        .map((item) => item.textContent.replace(/^\s*✓\s*/, '').trim())
        .filter((text) => {
          if (!text || seen.has(text)) return false;
          seen.add(text);
          return true;
        });

      if (!itemTexts.length) return null;

      source.className = 'ypb-goodstuff-banner';
      source.removeAttribute('data-ypb-detached');
      source.setAttribute('aria-label', headingText);
      source.innerHTML = '';

      const track = document.createElement('div');
      track.className = 'ypb-goodstuff-banner__track';
      track.appendChild(buildGroup(headingText, itemTexts, false));
      track.appendChild(buildGroup(headingText, itemTexts, true));
      source.appendChild(track);

      return source;
    }

    function placeBanner(banner) {
      if (!banner) return;
      const module = document.querySelector('ypb-purchase-module');
      const mainSection = module?.closest('.shopify-section');
      if (!mainSection) return;

      if (mainSection.nextElementSibling !== banner) {
        mainSection.insertAdjacentElement('afterend', banner);
      }
    }

    function initGoodStuffBanner() {
      const liveSource = document.querySelector('ypb-purchase-module .ypb-pm__formula');
      const existing = document.querySelector('.ypb-goodstuff-banner');

      if (liveSource) {
        if (existing && existing !== liveSource) existing.remove();
        placeBanner(convertFormula(liveSource));
        return;
      }

      if (existing) placeBanner(existing);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initGoodStuffBanner, { once: true });
    } else {
      initGoodStuffBanner();
    }

    document.addEventListener('shopify:section:load', () => {
      window.setTimeout(initGoodStuffBanner, 0);
    });
  })();

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class CurrencySelector extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if(!this.form) {
      throw new Error('No localization form found.');
    }
    if(!this.querySelector('input[name="currency"]')) {
      const input = document.createElement('input');
      input.setAttribute('name', 'currency');
      input.setAttribute('type', 'hidden');
      this.form.append(input)
    }
    const codes = this.querySelectorAll('[data-currency-code]');
    codes.forEach(i => {
      i.addEventListener('click', this.selectCurrency.bind(this));
    })
  }

  get form() {
    return this.querySelector('form');
  }

  get input() {
    return this.querySelector('input[name="currency"]');
  }

  selectCurrency(e) {
    const code = e.target.dataset.currencyCode;
    this.input.value = code;
    this.form.submit();
  }
}

customElements.define('currency-selector', CurrencySelector);

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('details-modal', DetailsModal);

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function calcHeight(element, container) {
  const clone = element.cloneNode(true);
  Object.assign(clone.style, {
    overflow: 'visible',
    height: 'auto',
    maxHeight: 'none',
    opacity: '0',
    visibility: 'hidden',
    display: 'block'
  });
  container.after(clone);
  const height = clone.offsetHeight;
  clone.remove();
  return height;
}

function horizontalLoop(items, config) {
  gsap.registerPlugin(Draggable)
  let timeline;
  items = gsap.utils.toArray(items);
  config = config || {};
  gsap.context(() => { // use a context so that if this is called from within another context or a gsap.matchMedia(), we can perform proper cleanup like the "resize" event handler on the window
    let onChange = config.onChange,
      lastIndex = 0,
      tl = gsap.timeline({repeat: config.repeat, onUpdate: onChange && function() {
          let i = tl.closestIndex();
          if (lastIndex !== i) {
            lastIndex = i;
            onChange(items[i], i);
          }
        }, paused: config.paused, defaults: {ease: "none"}, onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)}),
      length = items.length,
      startX = items[0].offsetLeft,
      times = [],
      widths = [],
      spaceBefore = [],
      xPercents = [],
      curIndex = 0,
      indexIsDirty = false,
      center = config.center,
      pixelsPerSecond = (config.speed || 1) * 100,
      snap = config.snap === false ? v => v : gsap.utils.snap(config.snap || 1), // some browsers shift by a pixel to accommodate flex layouts, so for example if width is 20% the first element's width might be 242px, and the next 243px, alternating back and forth. So we snap to 5 percentage points to make things look more natural
      timeOffset = 0,
      container = center === true ? items[0].parentNode : gsap.utils.toArray(center)[0] || items[0].parentNode,
      totalWidth,
      getTotalWidth = () => items[length-1].offsetLeft + xPercents[length-1] / 100 * widths[length-1] - startX + spaceBefore[0] + items[length-1].offsetWidth * gsap.getProperty(items[length-1], "scaleX") + (parseFloat(config.paddingRight) || 0),
      populateWidths = () => {
        let b1 = container.getBoundingClientRect(), b2;
        items.forEach((el, i) => {
          widths[i] = parseFloat(gsap.getProperty(el, "width", "px"));
          xPercents[i] = snap(parseFloat(gsap.getProperty(el, "x", "px")) / widths[i] * 100 + gsap.getProperty(el, "xPercent"));
          b2 = el.getBoundingClientRect();
          spaceBefore[i] = b2.left - (i ? b1.right : b1.left);
          b1 = b2;
        });
        gsap.set(items, { // convert "x" to "xPercent" to make things responsive, and populate the widths/xPercents Arrays to make lookups faster.
          xPercent: i => xPercents[i]
        });
        totalWidth = getTotalWidth();
      },
      timeWrap,
      populateOffsets = () => {
        timeOffset = center ? tl.duration() * (container.offsetWidth / 2) / totalWidth : 0;
        center && times.forEach((t, i) => {
          times[i] = timeWrap(tl.labels["label" + i] + tl.duration() * widths[i] / 2 / totalWidth - timeOffset);
        });
      },
      getClosest = (values, value, wrap) => {
        let i = values.length,
          closest = 1e10,
          index = 0, d;
        while (i--) {
          d = Math.abs(values[i] - value);
          if (d > wrap / 2) {
            d = wrap - d;
          }
          if (d < closest) {
            closest = d;
            index = i;
          }
        }
        return index;
      },
      populateTimeline = () => {
        let i, item, curX, distanceToStart, distanceToLoop;
        tl.clear();
        for (i = 0; i < length; i++) {
          item = items[i];
          curX = xPercents[i] / 100 * widths[i];
          distanceToStart = item.offsetLeft + curX - startX + spaceBefore[0];
          distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");
          tl.to(item, {xPercent: snap((curX - distanceToLoop) / widths[i] * 100), duration: distanceToLoop / pixelsPerSecond}, 0)
            .fromTo(item, {xPercent: snap((curX - distanceToLoop + totalWidth) / widths[i] * 100)}, {xPercent: xPercents[i], duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond, immediateRender: false}, distanceToLoop / pixelsPerSecond)
            .add("label" + i, distanceToStart / pixelsPerSecond);
          times[i] = distanceToStart / pixelsPerSecond;
        }
        timeWrap = gsap.utils.wrap(0, tl.duration());
      },
      refresh = (deep) => {
        let progress = tl.progress();
        tl.progress(0, true);
        populateWidths();
        deep && populateTimeline();
        populateOffsets();
        deep && tl.draggable ? tl.time(times[curIndex], true) : tl.progress(progress, true);
      },
      onResize = () => !config.disable_resize && refresh(true),
      proxy;
    gsap.set(items, {x: 0});
    populateWidths();
    populateTimeline();
    populateOffsets();
    window.addEventListener("resize", onResize);
    function toIndex(index, vars) {
      vars = vars || {};
      (Math.abs(index - curIndex) > length / 2) && (index += index > curIndex ? -length : length); // always go in the shortest direction
      let newIndex = gsap.utils.wrap(0, length, index),
        time = times[newIndex];
      if (time > tl.time() !== index > curIndex && index !== curIndex) { // if we're wrapping the timeline's playhead, make the proper adjustments
        time += tl.duration() * (index > curIndex ? 1 : -1);
      }
      if (time < 0 || time > tl.duration()) {
        vars.modifiers = {time: timeWrap};
      }
      curIndex = newIndex;
      vars.overwrite = true;
      gsap.killTweensOf(proxy);    
      return vars.duration === 0 ? tl.time(timeWrap(time)) : tl.tweenTo(time, vars);
    }
    tl.toIndex = (index, vars) => toIndex(index, vars);
    tl.closestIndex = setCurrent => {
      let index = getClosest(times, tl.time(), tl.duration());
      if (setCurrent) {
        curIndex = index;
        indexIsDirty = false;
      }
      return index;
    };
    tl.current = () => indexIsDirty ? tl.closestIndex(true) : curIndex;
    tl.next = vars => toIndex(tl.current()+1, vars);
    tl.previous = vars => toIndex(tl.current()-1, vars);
    tl.times = times;
    tl.progress(1, true).progress(0, true); // pre-render for performance
    if (config.reversed) {
      tl.vars.onReverseComplete();
      tl.reverse();
    }
    if (config.draggable && typeof(Draggable) === "function") {
      proxy = document.createElement("div")
      let wrap = gsap.utils.wrap(0, 1),
        ratio, startProgress, draggable, dragSnap, lastSnap, initChangeX, wasPlaying,
        align = () => tl.progress(wrap(startProgress + (draggable.startX - draggable.x) * ratio)),
        syncIndex = () => tl.closestIndex(true);
      typeof(InertiaPlugin) === "undefined" && console.warn("InertiaPlugin required for momentum-based scrolling and snapping. https://greensock.com/club");
      draggable = Draggable.create(proxy, {
        trigger: items[0].parentNode,
        type: "x",
        onPressInit() {
          let x = this.x;
          gsap.killTweensOf(tl);
          wasPlaying = !tl.paused();
          tl.pause();
          startProgress = tl.progress();
          refresh();
          ratio = 1 / totalWidth;
          initChangeX = (startProgress / -ratio) - x;
          gsap.set(proxy, {x: startProgress / -ratio});
        },
        onDrag: align,
        onThrowUpdate: align,
        overshootTolerance: 0,
        inertia: true,
        snap(value) {
          //note: if the user presses and releases in the middle of a throw, due to the sudden correction of proxy.x in the onPressInit(), the velocity could be very large, throwing off the snap. So sense that condition and adjust for it. We also need to set overshootTolerance to 0 to prevent the inertia from causing it to shoot past and come back
          if (Math.abs(startProgress / -ratio - this.x) < 10) {
            return lastSnap + initChangeX
          }
          let time = -(value * ratio) * tl.duration(),
            wrappedTime = timeWrap(time),
            snapTime = times[getClosest(times, wrappedTime, tl.duration())],
            dif = snapTime - wrappedTime;
          Math.abs(dif) > tl.duration() / 2 && (dif += dif < 0 ? tl.duration() : -tl.duration());
          lastSnap = (time + dif) / tl.duration() / -ratio;
          return lastSnap;
        },
        onRelease() {
          syncIndex();
          draggable.isThrowing && (indexIsDirty = true);
        },
        onThrowComplete: () => {
          syncIndex();
          wasPlaying && tl.play();
        }
      })[0];
      tl.draggable = draggable;
    }
    tl.closestIndex(true);
    lastIndex = curIndex;
    onChange && onChange(items[curIndex], curIndex);
    timeline = tl;
    return () => window.removeEventListener("resize", onResize); // cleanup
  });
  return timeline;
}

function getSectionInnerHTML(html, selector) {
  return new DOMParser()
    .parseFromString(html, 'text/html')
    .querySelector(selector).innerHTML;
}

function pauseAllMedia(element = null) {
  if(element == null) {
    element = document;
  }
  element.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  element.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  element.querySelectorAll('video').forEach((video) => video.pause());
  element.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

class DeferredMedia extends HTMLElement {
  connectedCallback() {
    this.poster = this.querySelector('[id^="Deferred-Poster-"]');
    this.shouldAutoPlay = this.dataset.shouldAutoplay;
    this.didAutoPlay = false;
    if(this.shouldAutoPlay) {
      window.setTimeout(() => {
        this.loadContent(false);
      }, 1000)
    }
    this.carousel = this.closest('carousel-block');
    this.carousel.addEventListener('slide', this.toggleAutoPlay.bind(this))
    if (!this.poster) return;
    this.poster.addEventListener('click', this.loadContent.bind(this));
  }
  
  toggleAutoPlay() {
    if(this.carousel.isVisible(this) && this.deferredElement.getAttribute('autoplay')) {
      this.deferredElement.play();
    }
    else {
      this.deferredElement.pause();
    }
  }

  loadContent(focus = true) {
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', true);
      this.poster.querySelector('img').classList.add('blur-md');
      this.deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (this.deferredElement.nodeName == 'VIDEO' && this.deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        this.deferredElement.muted = true;
        this.deferredElement.play();
      }

    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

if (!customElements.get('share-button')) {
  customElements.define(
    'share-button',
    class ShareButton extends DetailsDisclosure {
      constructor() {
        super();

        this.elements = {
          shareButton: this.querySelector('button'),
          shareSummary: this.querySelector('summary'),
          closeButton: this.querySelector('.share-button__close'),
          successMessage: this.querySelector('[id^="ShareMessage"]'),
          urlInput: this.querySelector('input'),
        };
        this.urlToShare = this.elements.urlInput ? this.elements.urlInput.value : document.location.href;

        if (navigator.share) {
          this.mainDetailsToggle.setAttribute('hidden', '');
          this.elements.shareButton.classList.remove('hidden');
          this.elements.shareButton.addEventListener('click', () => {
            navigator.share({ url: this.urlToShare, title: document.title });
          });
        } else {
          this.mainDetailsToggle.addEventListener('toggle', this.toggleDetails.bind(this));
          this.mainDetailsToggle
            .querySelector('.share-button__copy')
            .addEventListener('click', this.copyToClipboard.bind(this));
          this.mainDetailsToggle.querySelector('.share-button__close').addEventListener('click', this.close.bind(this));
        }
      }

      toggleDetails() {
        if (!this.mainDetailsToggle.open) {
          this.elements.successMessage.classList.add('hidden');
          this.elements.successMessage.textContent = '';
          this.elements.closeButton.classList.add('hidden');
          this.elements.shareSummary.focus();
        }
      }

      copyToClipboard() {
        navigator.clipboard.writeText(this.elements.urlInput.value).then(() => {
          this.elements.successMessage.classList.remove('hidden');
          this.elements.successMessage.textContent = 'Link copied to clipboard';
          this.elements.closeButton.classList.remove('hidden');
          this.elements.closeButton.focus();
        });
      }

      updateUrl(url) {
        this.urlToShare = url;
        this.elements.urlInput.value = url;
      }
    }
  );
}

const waitForImageToLoad = (imageElement) => {
  return new Promise(resolve=> {
    const src = imageElement.src;
    imageElement.onload = resolve
    imageElement.src = src;
  })
}

window.onload = () => {
  pauseAllMedia();
  pageSetup();
}

gsap.registerPlugin(ScrollSmoother, ScrollTrigger);

const createSmoothScroll = () => {
  ScrollSmoother.create({
    smooth: 2,
    effects: true,
    normalizeScroll: { allowNestedScroll: true },
    ignoreMobileResize: true,
    wholePixels: true
  });
}

const pageSetup = () => {
  createSmoothScroll();
  ScrollTrigger.create({
    trigger: '#navbar-anchor',
    start: 'top top+=200',
    scrub: true,
    onEnter: () => {
      const fixedHeader = document.querySelector('.fixed-header');
      const navbar = fixedHeader.querySelector('nav-menu');
      const wasOverlay = navbar?.dataset.position === 'overlay';
      navbar?.setAttribute('data-was-overlay', wasOverlay);
      navbar?.setAttribute('data-position', 'standard');
      // document.getElementById('smooth-margin').style.paddingTop = `${fixedHeader.offsetHeight}px`;
      
      if(!wasOverlay) {
        gsap.set('#smooth-margin', {
          paddingTop: fixedHeader.offsetHeight
        })
      }
      gsap.set(fixedHeader, {
        position: 'fixed',
        zIndex: 3,
        yPercent: -100
      })
      document.body.prepend(fixedHeader);
      gsap.to(fixedHeader, {
        yPercent: 0,
        duration: 0.4
      })
    },
    onEnterBack: () => {
      const fixedHeader = document.querySelector('.fixed-header');
      const navbar = fixedHeader.querySelector('nav-menu');
      const wasOverlay = navbar?.dataset.wasOverlay === 'true';
      gsap.to(fixedHeader, {
        yPercent: -100,
        duration: 0.4,
        onComplete: () => {
          navbar?.setAttribute('data-position', wasOverlay ? 'overlay': 'standard');
          gsap.set(fixedHeader, {
            position: 'relative',
            zIndex: 3,
            yPercent: 0
          });
          if(!wasOverlay) {
            gsap.set('#smooth-margin', {
              paddingTop: 0
            })
          }
          document.getElementById('smooth-content').prepend(fixedHeader);
        }
      })
    }
  });

}

const list = document.querySelector('[id$="-listbox"]');

list.addEventListener('mouseenter', () => {
  ScrollSmoother.get()?.paused(true);
});

list.addEventListener('mouseleave', () => {
  ScrollSmoother.get()?.paused(false);
});
